import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FacadeService } from 'src/app/core/services/facade.service';
import { ImportResponse } from 'src/app/shared/models/api-response/import-response';
import { BonCommande } from 'src/app/shared/models/bonCommande';
import { saveAs } from 'file-saver';
import { PARAM_LABEL_BENEFICIAIRE, PARAM_LABEL_USER } from 'src/app/core/services/constant/constants.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ErrorResponse } from 'src/app/shared/models/api-response/error-response';
import {
	CompetenceElementaireSESSAD,
	CompetenceSAAD,
	CompetenceSSIAD
} from 'src/app/shared/enums/competence.enum';
import { Subject, debounceTime, take } from 'rxjs';
import { LogService } from 'src/app/core/services/log/log.service';
import { ListingResponse } from 'src/app/shared/models/listing/listing-response';
import { BonCommandeModalAjoutFormComponent } from '../modal/user-modal-ajout-form/bonCommande-modal-ajout-form.component';

export type SortOrder = 'asc' | 'desc';

@Component({
	selector: 'app-list-bonCommande',
	templateUrl: './list-bonCommande.component.html',
	styleUrls: ['./list-bonCommande.component.scss']
})
export class ListBonCommandeComponent implements OnInit {
	bonCommandes: BonCommande[];
	currentPage = 1;

	bonCommandeCsvFile: File = null;
	sortType = 'lastName';
	sortOrder: SortOrder = 'asc';

	nextWeekNumber: number;
	competences: Record<string, string>;

	searchedInput = '';

	// nombre de page totale pour la pagination
	totalItems: number = 0;
	itemsPerPage = 10;

	bonCommande: BonCommande = null;
	isPlanif = false;
	labelBonCommandeCapitalized: string;
	labelBonCommandePluralized: string;

	ajoutBonCommandeModalref: BsModalRef;

	isAddable = false;
	isImportable = false;
	isDownloadable = false
	isExportable = false;

	isMobile = false;
	screenWidth: number;

	isSESSAD = false;
	isSAAD = false;
	isSSIAD = false;
	isAreaActive: boolean;

	isListingLoading: boolean = true;
	isAdmin: boolean;

	private searchSubject = new Subject<string>();

	constructor(
		private router: Router,
		private facade: FacadeService,
		private modalService: BsModalService,
		private logger: LogService
	) {
		// ajout de délai à la saisie de recherche pour ne pas bombarder le back de requête
		this.searchSubject
			.pipe(
				debounceTime(300) // Délai en millisecondes
			)
			.subscribe(() => {
				this.searchByInput();
			});
	}

	@HostListener('window:resize', ['$event'])
	onWindowResize() {
		this.screenWidth = window.innerWidth;
		if (this.screenWidth < 1200) {
			this.isMobile = true;
		} else {
			this.isMobile = false;
		}
	}

	ngOnInit(): void {
		this.screenWidth = window.innerWidth;
		this.isMobile =
			this.facade.mobileService.isMobile() || this.screenWidth < 1200;

		this.labelBonCommandeCapitalized =
			this.facade.dataStorageService.getLabelCapitalized(
				PARAM_LABEL_USER
			);
		this.labelBonCommandePluralized =
			this.facade.dataStorageService.getLabelPluralized(
				PARAM_LABEL_USER
			);

		this.competences = this.isSESSAD
			? CompetenceElementaireSESSAD
			: this.isSAAD
			? CompetenceSAAD
			: CompetenceSSIAD;

		this.isExportable = false;
		this.isAddable = true;
		this.isDownloadable = true;
		this.isImportable = false;
		this.isAdmin = this.facade.dataStorageService.isAdmin();

		this.getBonCommandesMinimal();
	}

	getBonCommandesMinimal(): void {
		this.isListingLoading = true;
		if(this.isAdmin){
			this.facade.bonCommandeService.getAllBonCommandes()
				.subscribe({
					next: (val) => {
						this.bonCommandes = val;
						this.totalItems = this.bonCommandes.length >= 8 ? 10 : 8;
					},
					error: (err: ErrorResponse) => {
						this.logger.error(err + '');
					},
					complete: () => {
						this.isListingLoading = false;
					}
				});
		}else{
			this.facade.bonCommandeService.getBonCommandeByServiceAndRegion(this.facade.dataStorageService.getService()?.id, this.facade.dataStorageService.getRegion()?.id)
				.subscribe({
					next: (val) => {
						this.bonCommandes = val;
						this.totalItems = this.bonCommandes.length >= 8 ? 10 : 8;
					},
					error: (err: ErrorResponse) => {
						this.logger.error(err + '');
					},
					complete: () => {
						this.isListingLoading = false;
					}
				});
		}
	}

	selectBonCommande(bonCommande: BonCommande): void {
		const link = ['/gestion/bonCommandes', bonCommande.id];
		this.router.navigate(link);
	}

	importBonCommandes(event: any): void {
		this.isImportable = false;
		this.bonCommandeCsvFile = event.target.files[0];
		this.facade.bonCommandeService
			.importBonCommandes(this.bonCommandeCsvFile)
			.subscribe({
				next: (importStats: ImportResponse) => {
					this.facade.toastService.createSuccessToast('Import effectué');
					this.getBonCommandesMinimal();
					this.isImportable = true;
				},
				error: (err: ErrorResponse) => {
					this.isImportable = true;
					this.facade.toastService.createHttpErrorToast(
						err.error.message ? err.error.message : '',
						err.status
					);
				}
			});
	}

	exportBonCommandes(): void {
		this.facade.exportService
			.exportBonCommandes()
			.subscribe((blob) => saveAs(blob, 'bonCommande.csv'));
	}

	/**
	 * Fonction pour les paramètres de trie. Si le string choisi vaut le string courant l'on change l'ordre de trie (croissant/décroissant)
	 * sinon l'on chance la colonne de trie et l'on trie par ordre crosisant
	 * @param str colonne de trie
	 */
	sortChange(str: string): void {
		if (str === this.sortType) {
			if (this.sortOrder === 'asc') {
				this.sortOrder = 'desc';
			} else {
				this.sortOrder = 'asc';
			}
		} else {
			this.sortType = str;
			this.sortOrder = 'asc';
		}
	}

	searchByInput() {
		this.currentPage = 1;
		this.getBonCommandesMinimal();
	}

	downloadLastSynthesis(){
		this.isListingLoading = true;
		let dateFin = new Date(Date.now()).toDateString();
		let dateDebut 
		let currentDate = new Date();
		currentDate.setMonth(currentDate.getMonth()-1)
		currentDate.setDate(21);
		dateDebut = new Date(currentDate).toDateString();
		this.facade.institutionService.downloadLastSynthesis(this.facade.dataStorageService.getService()?.id, this.facade.dataStorageService.getRegion()?.id,
			dateDebut, dateFin).subscribe(	
				(blob) => {
					saveAs(blob, "syntheseBdc.docx")
					this.isListingLoading = false
				},
				async (err) => {
					this.isListingLoading = false;
					this.facade.toastService.createHttpErrorToast(
						err.error.message ? err.error.message : '',
						err.status
					);
				  });
				
	}

	openModalAjout() {
		this.facade.authenticationService.checkIsAuthenticated();
		// const initialState = {
		// 	serviceCenter: this.bonCommande.employee.serviceCenter
		// };
		// if (this.bonCommande === null || this.bonCommande.employee.serviceCenter === null) {
		// 	this.facade.toastService.createErrorToast(
		// 		"Vous n'êtes pas rattaché à un centre."
		// 	);
		// } else {
			this.ajoutBonCommandeModalref = this.modalService.show(
				BonCommandeModalAjoutFormComponent,
				{
					class: 'modal-lg',
					//initialState,
					backdrop: 'static'
				}
			);
			this.ajoutBonCommandeModalref.content.updateListing
				.pipe(take(1))
				.subscribe((addedBonCommande: BonCommande) => {
					this.bonCommandes.push(addedBonCommande);
				});
		//}
	}

	pageHasChanged(pageNumber: any) {
		this.currentPage = pageNumber;

		this.getBonCommandesMinimal();
	}
}
